import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import api, { itineraryApi } from '../services/api';
import { downloadBlogPdf, getBlogPdfFileName } from '../utils/downloadBlogPdf';
import { BlogShareDialog } from '../components/blog/BlogShareDialog';
import {
  Loader2, ArrowLeft, Edit3, Save, X, Share2,
  FileText, Sparkles, Trash2, Upload, Image as ImageIcon, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TripBlogPage = () => {
  const { id: itineraryId } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editSubtitle, setEditSubtitle] = useState('');
  const [editSections, setEditSections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [regeneratingCover, setRegeneratingCover] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const loadBlog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await itineraryApi.getBlog(itineraryId);
      if (res.data.success && res.data.blog) {
        setBlog(res.data.blog);
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
    // itineraryApi, setBlog, setLoading are stable refs
  }, [itineraryId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadBlog();
  }, [loadBlog]);

  const handleCreateBlog = async () => {
    setGenerating(true);
    try {
      const res = await itineraryApi.generateBlog(itineraryId);
      if (res.data.success) {
        setBlog(res.data.blog);
        toast.success('Blog created!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create blog');
    } finally {
      setGenerating(false);
    }
  };

  const handleStartEdit = () => {
    setEditTitle(blog.title || '');
    setEditSubtitle(blog.subtitle || '');
    setEditSections(blog.sections?.map(s => ({ ...s, images: s.images ? [...s.images] : [] })) || []);
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await itineraryApi.saveBlog(itineraryId, {
        title: editTitle,
        subtitle: editSubtitle,
        sections: editSections,
      });
      setBlog(prev => ({ ...prev, title: editTitle, subtitle: editSubtitle, sections: editSections }));
      setEditing(false);
      toast.success('Blog saved!');
    } catch {
      toast.error('Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (idx, field, value) => {
    setEditSections(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const deleteSection = (idx) => {
    setEditSections(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUploadImage = async (sectionIdx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploading(sectionIdx);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('day_number', '1');
    formData.append('caption', '');
    try {
      const res = await api.post(`/journal/${itineraryId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        const photo = res.data.photo;
        setEditSections(prev => {
          const updated = [...prev];
          const images = updated[sectionIdx].images ? [...updated[sectionIdx].images] : [];
          images.push({ storage_path: photo.storage_path, caption: photo.caption || '' });
          updated[sectionIdx] = { ...updated[sectionIdx], images };
          return updated;
        });
        toast.success('Image uploaded!');
      }
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setUploading(null);
      e.target.value = '';
    }
  };

  const removeImage = (sectionIdx, imgIdx) => {
    setEditSections(prev => {
      const updated = [...prev];
      const images = [...(updated[sectionIdx].images || [])];
      images.splice(imgIdx, 1);
      updated[sectionIdx] = { ...updated[sectionIdx], images };
      return updated;
    });
  };

  const handleShareBlog = async () => {
    setSharing(true);
    try {
      const res = await itineraryApi.createBlogShare(itineraryId);
      if (res.data.success) {
        const url = `${window.location.origin}/shared/blog/${res.data.share_id}`;
        setShareUrl(url);
        setShowShareDialog(true);
      }
    } catch {
      toast.error('Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  const handleDownloadPdf = () => {
    if (!blog) return;
    downloadBlogPdf(blog);
    toast.success('Generating PDF...');
  };

  const handleRegenerateCover = async () => {
    setRegeneratingCover(true);
    try {
      const res = await itineraryApi.generateBlogCover(itineraryId);
      if (res.data.success) {
        setBlog(prev => ({ ...prev, cover_image: res.data.cover_image }));
        toast.success('Cover image regenerated!');
      }
    } catch {
      toast.error('Failed to generate cover image');
    } finally {
      setRegeneratingCover(false);
    }
  };

  const handleUploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploadingCover(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('day_number', '0');
    formData.append('caption', 'Blog cover');
    try {
      const res = await api.post(`/journal/${itineraryId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        const coverPath = res.data.photo.storage_path;
        await itineraryApi.saveBlog(itineraryId, { cover_image: coverPath });
        setBlog(prev => ({ ...prev, cover_image: coverPath }));
        toast.success('Cover image uploaded!');
      }
    } catch { toast.error('Failed to upload cover'); }
    finally { setUploadingCover(false); e.target.value = ''; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70">
        <Header />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gold" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="trip-blog-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B3C5D] via-[#0d4a6b] to-[#1a6b8a]" />
        <div className="absolute top-20 right-10 w-96 h-96 bg-gold/8 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-sky/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <Header />

      <main className="pt-24 pb-16 px-4 sm:px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-3xl mx-auto">
          {/* Top bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
            <Link to={`/itinerary/${itineraryId}`} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-itinerary">
              <ArrowLeft className="w-4 h-4" /> Back to Itinerary
            </Link>
            {blog && (
              <div className="flex flex-wrap gap-2">
                {editing ? (
                  <>
                    <Button onClick={handleSave} disabled={saving} className="bg-forest hover:bg-forest/90 text-white rounded-full text-sm font-semibold shadow-lg" data-testid="save-blog-btn">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save
                    </Button>
                    <Button onClick={handleCancelEdit} variant="outline" className="text-white border-white/30 hover:bg-white/10 rounded-full text-sm" data-testid="cancel-edit-btn">
                      <X className="w-4 h-4 mr-2" /> Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button onClick={handleStartEdit} className="bg-white/20 hover:bg-white/30 text-white rounded-full text-sm font-semibold shadow-lg" data-testid="edit-blog-btn">
                      <Edit3 className="w-4 h-4 mr-2" /> Edit
                    </Button>
                    <Button onClick={handleShareBlog} disabled={sharing} className="bg-gold hover:bg-gold/90 text-ocean rounded-full text-sm font-semibold shadow-lg" data-testid="share-blog-btn">
                      {sharing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
                      Share Blog
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* No blog yet — create prompt */}
          {!blog && (
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-gold" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-white mb-3">Create Your Travel Blog</h2>
              <p className="text-white/60 max-w-md mx-auto mb-8">Transform your trip into a captivating blog story. AI will craft a personalized narrative from your itinerary, activities, and story notes.</p>
              <Button
                onClick={handleCreateBlog}
                disabled={generating}
                className="bg-gold hover:bg-gold/90 text-ocean rounded-full px-8 py-4 text-base font-semibold shadow-xl"
                data-testid="create-blog-btn"
              >
                {generating ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Creating Blog...</>
                ) : (
                  <><Sparkles className="w-5 h-5 mr-2" /> Create a Blog</>
                )}
              </Button>
            </motion.div>
          )}

          {/* Blog content */}
          {blog && (
            <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
              {/* Cover Image */}
              {blog.cover_image && (
                <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-testid="blog-cover-image">
                  {blog.cover_image.startsWith('triplea-travel/') ? (
                    <img
                      src={`${API_URL}/api/blog/cover/${encodeURIComponent(blog.cover_image)}`}
                      alt="Blog cover"
                      className="w-full h-48 sm:h-64 lg:h-80 object-cover"
                    />
                  ) : (
                    <img
                      src={`${API_URL}/api/journal/file/${encodeURIComponent(blog.cover_image)}`}
                      alt="Blog cover"
                      className="w-full h-48 sm:h-64 lg:h-80 object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  {editing && (
                    <div className="absolute bottom-3 right-3 flex gap-2">
                      <Button
                        onClick={handleRegenerateCover}
                        disabled={regeneratingCover}
                        size="sm"
                        className="bg-white/90 hover:bg-white text-ocean rounded-full text-xs h-8 px-3 shadow"
                        data-testid="regenerate-cover-btn"
                      >
                        {regeneratingCover ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                        Regenerate
                      </Button>
                      <label className="cursor-pointer inline-flex items-center bg-white/90 hover:bg-white text-ocean rounded-full text-xs h-8 px-3 shadow font-medium" data-testid="upload-cover-btn">
                        <input type="file" accept="image/*" className="hidden" onChange={handleUploadCover} disabled={uploadingCover} />
                        {uploadingCover ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                        Upload
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* No cover yet + editing = show generate button */}
              {!blog.cover_image && editing && (
                <div className="border-2 border-dashed border-white/20 rounded-2xl p-8 text-center" data-testid="no-cover-placeholder">
                  <ImageIcon className="w-10 h-10 text-white/30 mx-auto mb-3" />
                  <p className="text-white/50 text-sm mb-4">No cover image yet</p>
                  <div className="flex gap-2 justify-center">
                    <Button
                      onClick={handleRegenerateCover}
                      disabled={regeneratingCover}
                      size="sm"
                      className="bg-gold hover:bg-gold/90 text-ocean rounded-full text-xs px-4"
                      data-testid="generate-cover-btn"
                    >
                      {regeneratingCover ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      Generate Cover
                    </Button>
                    <label className="cursor-pointer inline-flex items-center bg-white/20 hover:bg-white/30 text-white rounded-full text-xs h-8 px-4 font-medium">
                      <input type="file" accept="image/*" className="hidden" onChange={handleUploadCover} disabled={uploadingCover} />
                      {uploadingCover ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                      Upload Cover
                    </label>
                  </div>
                </div>
              )}

              {/* Title */}
              {editing ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-5 py-3 text-white text-2xl sm:text-3xl font-serif focus:outline-none focus:ring-2 focus:ring-gold/40"
                    placeholder="Blog title"
                    data-testid="edit-blog-title"
                  />
                  <input
                    type="text"
                    value={editSubtitle}
                    onChange={(e) => setEditSubtitle(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-5 py-2 text-white/70 text-base focus:outline-none focus:ring-2 focus:ring-gold/40"
                    placeholder="Subtitle"
                    data-testid="edit-blog-subtitle"
                  />
                </div>
              ) : (
                <div className="text-center mb-4">
                  <p className="text-gold text-sm tracking-[0.2em] uppercase font-semibold mb-3">Travel Blog</p>
                  <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white drop-shadow-lg mb-3 leading-tight" data-testid="blog-title">{blog.title}</h1>
                  {blog.subtitle && <p className="text-white/50 text-lg italic">{blog.subtitle}</p>}
                  <div className="w-16 h-0.5 bg-gold/40 mx-auto mt-6" />
                </div>
              )}

              {/* Sections */}
              {editing ? (
                <div className="space-y-6">
                  {editSections.map((section, idx) => (
                    <div key={`edit-section-${section.heading || idx}`} className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl p-5 space-y-3 relative group">
                      {/* Delete section button */}
                      <button
                        onClick={() => deleteSection(idx)}
                        className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500/20 hover:bg-red-500/40 flex items-center justify-center text-red-300 hover:text-red-200 transition-colors"
                        title="Delete section"
                        data-testid={`delete-section-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>

                      <input
                        type="text"
                        value={section.heading}
                        onChange={(e) => updateSection(idx, 'heading', e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-gold font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-gold/40 pr-12"
                        placeholder="Section heading"
                        data-testid={`edit-section-heading-${idx}`}
                      />
                      <textarea
                        value={section.content}
                        onChange={(e) => updateSection(idx, 'content', e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white/90 text-sm leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-gold/40 min-h-[140px]"
                        placeholder="Section content"
                        rows={6}
                        data-testid={`edit-section-content-${idx}`}
                      />

                      {/* Section images */}
                      {section.images?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {section.images.map((img, imgIdx) => (
                            <div key={`edit-img-${img.storage_path}`} className="relative w-24 h-20 rounded-lg overflow-hidden group/img">
                              <img
                                src={`${API_URL}/api/journal/file/${encodeURIComponent(img.storage_path)}`}
                                alt={img.caption || 'Blog image'}
                                className="w-full h-full object-cover"
                              />
                              <button
                                onClick={() => removeImage(idx, imgIdx)}
                                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Upload image button */}
                      <label className="cursor-pointer inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-4 py-2 text-white/70 text-xs transition-colors" data-testid={`upload-image-${idx}`}>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => handleUploadImage(idx, e)}
                          disabled={uploading === idx}
                        />
                        {uploading === idx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {uploading === idx ? 'Uploading...' : 'Upload Image'}
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-10">
                  {blog.sections?.map((section, idx) => (
                    <motion.section
                      key={`section-${section.heading || idx}`}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.08 * idx }}
                      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8"
                      data-testid={`blog-section-${idx}`}
                    >
                      <h2 className="font-serif text-xl sm:text-2xl text-gold mb-4">{section.heading}</h2>

                      {/* Section images */}
                      {section.images?.length > 0 && (
                        <div className={`mb-4 grid gap-2 ${section.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                          {section.images.map((img, imgIdx) => (
                            <div key={`view-img-${img.storage_path}`} className="rounded-xl overflow-hidden h-40 sm:h-52">
                              <img
                                src={`${API_URL}/api/journal/file/${encodeURIComponent(img.storage_path)}`}
                                alt={img.caption || 'Blog image'}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="text-white/80 text-sm sm:text-base leading-relaxed whitespace-pre-line break-words">
                        {section.content}
                      </div>
                    </motion.section>
                  ))}
                </div>
              )}

              {/* Bottom actions */}
              <div className="flex flex-wrap items-center justify-center gap-3 pt-6">
                <Button
                  onClick={() => navigate(`/itinerary/${itineraryId}`)}
                  variant="outline"
                  className="text-white border-white/30 hover:bg-white/10 rounded-full px-6"
                  data-testid="back-to-itinerary-bottom"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Itinerary
                </Button>
                {editing && (
                  <Button onClick={handleSave} disabled={saving} className="bg-gold hover:bg-gold/90 text-ocean rounded-full px-6 font-semibold shadow-lg" data-testid="save-blog-bottom-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Blog
                  </Button>
                )}
              </div>
            </motion.article>
          )}
        </div>
      </main>

      <BlogShareDialog
        show={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        shareUrl={shareUrl}
        blog={blog}
        pdfFileName={getBlogPdfFileName(blog)}
        onDownloadPdf={handleDownloadPdf}
      />
    </div>
  );
};

export default TripBlogPage;
